import '@/modules/system/auth/presentation/pages/escolha-modulo.css';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Links from '@/shared/constants/navegacao';
import { CorregedoriaCheck } from '@/modules/reports/application/relatorios-corregedoria.service';
import { GetDiretoriaDoUsuario } from '@/modules/structure/diretoria/diretoria.service';
import { CarregarPermissoes, ValidarPermissao } from '@/modules/system/auth/application/auth.service';
import { RoleEnum } from '@/modules/system/papeis/enum/role.enum';
import { PermissionEnum } from '@/modules/system/papeis/enum/permission.enum';
import RelatoriosUserLabel from '@/modules/reports/presentation/components/RelatoriosUserLabel';
import { ToastContainer } from 'react-toastify';
import { LuArrowLeftRight, LuScrollText, LuScale, LuGavel, LuLogOut } from 'react-icons/lu';

type CardLinkProps = {
  title: string;
  description: string;
  to?: string;
  onClick?: () => void;
  icon: React.ReactNode;
  disabled?: boolean;
};

function CardLink({ title, description, to, onClick, disabled, icon }: CardLinkProps) {
  const body = (
    <button
      type="button"
      className="escolha-modulo-card"
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      aria-describedby="escolha-relatorios"
    >
      <span className="escolha-modulo-card-icon">{icon}</span>
      <span className="escolha-modulo-card-body">
        <span className="escolha-modulo-card-title">{title}</span>
        <p className="escolha-modulo-card-desc">{description}</p>
      </span>
    </button>
  );

  if (disabled || !to) return body;
  return (
    <Link to={to} className="text-decoration-none">
      {body}
    </Link>
  );
}

export default function RelatoriosMenuPage() {
  const navigate = useNavigate();
  const [isCorregedoria, setIsCorregedoria] = useState(false);
  const [hasDiretoria, setHasDiretoria] = useState(false);

  const permissoes = useMemo(() => CarregarPermissoes(), []);
  const isAdminSuporte = useMemo(
    () => ValidarPermissao(permissoes, [{ role: RoleEnum.ADMIN_ACTIONS, level: PermissionEnum.READ }]),
    [permissoes],
  );

  const canDiretoria = hasDiretoria || isAdminSuporte;
  const canCorregedoria = (isCorregedoria && !hasDiretoria) || isAdminSuporte;

  useEffect(() => {
    CorregedoriaCheck().then(setIsCorregedoria);
  }, []);

  useEffect(() => {
    GetDiretoriaDoUsuario()
      .then((diretoria) => setHasDiretoria(!!diretoria))
      .catch(() => setHasDiretoria(false));
  }, []);

  return (
    <>
      <ToastContainer position="top-center" />
      <section className="ftco-section">
        <div className="loginrow w-100 justify-content-center">
          <div className="escolha-modulo-wrap">
            <header className="escolha-modulo-heading">
              <h2>Relatórios de Atividades</h2>
              <RelatoriosUserLabel />
              <div className="d-flex align-items-start justify-content-between gap-2">
                <p id="escolha-relatorios" className="mb-0">
                  Selecione abaixo a área do módulo que deseja acessar nesta sessão.
                </p>
                <a href={Links.Auth_Logout} className="escolha-modulo-logout" title="Sair">
                  <LuLogOut size={18} strokeWidth={1.9} aria-hidden />
                  Sair
                </a>
              </div>
            </header>

            <ul className="escolha-modulo-grid">
              <li>
                <CardLink
                  title="Mudar Módulo"
                  description="Voltar para a tela de escolha de módulo do SGI."
                  onClick={() => navigate(Links.Escolha_Modulo)}
                  icon={<LuArrowLeftRight size={42} strokeWidth={1.35} aria-hidden />}
                />
              </li>
              <li>
                <CardLink
                  title="Meus Relatórios"
                  description="Criar, editar e enviar seus relatórios mensais."
                  to={Links.Relatorios_Meus}
                  icon={<LuScrollText size={42} strokeWidth={1.35} aria-hidden />}
                />
              </li>
              {canDiretoria &&
                <li>
                  <CardLink
                    title="Diretoria Regional"
                    description={canDiretoria ? 'Aprovação/reprovação dos relatórios na diretoria.' : 'Sem permissão para acessar.'}
                    to={Links.Relatorios_Aprovacao}
                    disabled={!canDiretoria}
                    icon={<LuScale size={44} strokeWidth={1.35} aria-hidden />}
                  />
                </li>
              }
              {canCorregedoria &&
                <li>
                  <CardLink
                    title="Corregedoria"
                    description={canCorregedoria ? 'Aval final e tramitação na Corregedoria.' : 'Sem permissão para acessar.'}
                    to={Links.Relatorios_Corregedoria}
                    disabled={!canCorregedoria}
                    icon={<LuGavel size={44} strokeWidth={1.35} aria-hidden />}
                  />
                </li>
              }
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}

